/**
 * @description API service for Lynx Websites (CMS) merchant UI (LYN-21)
 *
 * Key features:
 * - CMS feature access check for tab visibility
 * - Company-scoped site status, URLs, and provisioning state
 *
 * @dependencies
 * - apiClient: Authenticated axios instance with X-Company-Id header
 *
 * @notes
 * - Throws on API errors — callers must surface failures (no silent empty defaults)
 */

import apiClient from './apiClient';

const cmsService = {
  /**
   * Whether the authenticated user may access CMS features
   * @returns {Promise<{ allowed: boolean, reason?: string|null, source?: string|null }>}
   */
  getCmsAccess: async () => {
    const response = await apiClient.get('/features/cms-access');
    return response.data.data;
  },

  /**
   * CMS site status for the active company (requires X-Company-Id)
   * @returns {Promise<object>} Full cms-site payload from backend
   */
  getCmsSiteStatus: async () => {
    const response = await apiClient.get('/features/cms-site');
    return response.data.data;
  },

  /**
   * Provision CMS site for the active company (requires X-Company-Id)
   * @returns {Promise<object>} Same cms-site payload as getCmsSiteStatus on success
   */
  provisionCmsSite: async () => {
    const response = await apiClient.post('/features/cms-site/provision');
    return response.data.data;
  },

  /**
   * Custom domain connect status (polls verification when pending)
   * @returns {Promise<object>}
   */
  getCmsDomainStatus: async () => {
    const response = await apiClient.get('/features/cms-site/domain');
    return response.data.data;
  },

  /**
   * Register apex custom hostname with Cloudflare
   * @param {string} domain
   * @returns {Promise<object>}
   */
  connectCmsDomain: async (domain) => {
    const response = await apiClient.post(
      '/features/cms-site/domain/connect',
      { domain },
      // 120s: CF create 15s + adopt; SO registers apex then www
      { timeout: 120000 }
    );
    return response.data.data;
  },

  /**
   * Disconnect custom hostname (revokes edge KV + Cloudflare, clears cms_sites)
   * @returns {Promise<object>}
   */
  disconnectCmsDomain: async () => {
    const response = await apiClient.post('/features/cms-site/domain/disconnect');
    return response.data.data;
  }
};

export default cmsService;
